import { Client } from "graphql-ws";
import { ApolloClient, ApolloLink, InMemoryCache, HttpLink } from "@apollo/client";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { LocalState } from "@apollo/client/local-state";
import { ErrorLink } from "@apollo/client/link/error";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { AuthRoutes } from "../auth/AuthRoutes";
import oAuthClient from "../auth/oauth/instance";
import logger from "../utils/logs/LoggerInstance";
import fragmentTypes from "./fragmentTypes.json";
import typePolicies from "./typePolicies";
import { VITE_REACT_APP_API_GRAPHQL, VITE_REACT_APP_WS_GRAPHQL } from "../constants/variables";
import { makeAuthLink, redirectExpiredSessionToLogin } from "./authLink";
import { createAuthenticatedSubscriptionsClient } from "./subscriptionsAuth";

export const uploadLink = new HttpLink({
  uri: VITE_REACT_APP_API_GRAPHQL,
  headers: {
    "Apollo-Require-Preflight": "any",
    "X-apollo-operation-name": "any",
  },
});

let isRedirectingToLogout = false;
const goToLogout = () => {
  if (
    isRedirectingToLogout ||
    window.location.pathname.startsWith(AuthRoutes.BASEPATH)
  ) {
    return;
  }
  isRedirectingToLogout = true;
  window.location.replace(AuthRoutes.BASEPATH + AuthRoutes.LOGOUT);
};

export const authLink = makeAuthLink(oAuthClient);

const possibleTypes: {
  Connection: string[];
  Item: string[];
  OCRResult: string[];
} = {
  Connection: [],
  Item: [],
  OCRResult: [],
};
possibleTypes.Connection = (fragmentTypes as any).possibleTypes.Connection;
possibleTypes.Item = (fragmentTypes as any).possibleTypes.Item;
possibleTypes.OCRResult = (fragmentTypes as any).possibleTypes.OCRResult;

export const makeSubscriptionsClient = (
  {
    reconnect,
    authClient = oAuthClient,
    onExpiredSession = redirectExpiredSessionToLogin,
  }: {
    reconnect: boolean;
    authClient?: typeof oAuthClient;
    onExpiredSession?: () => void;
  },
) => createAuthenticatedSubscriptionsClient(authClient, {
  url: VITE_REACT_APP_WS_GRAPHQL,
  reconnect,
  onExpiredSession,
});

export const makeSubscriptionsLink = (wsClient: Client) => new GraphQLWsLink(wsClient);

export const wsClient = makeSubscriptionsClient({ reconnect: true });

const wsLink = makeSubscriptionsLink(wsClient); // new WebSocketLink(wsClient);

export const errorLink = new ErrorLink(({ error }) => {
  if (CombinedGraphQLErrors.is(error)) {
    const err = error.errors[0];
    if (err) {
      logger.error(`${err.message}`, ["errorLink", "graphqlError"], {
        originalError: err,
      });

      if (err.extensions?.code === "UNAUTHENTICATED") {
        goToLogout();
      }
    }
    return;
  }
  if (error) {
    logger.error(`${error.message}`, ["errorLink", "networkError"], {
      originalError: error,
    });
  }
});

const cache = new InMemoryCache({
  possibleTypes,
  typePolicies,
});

const client = new ApolloClient({
  cache,
  localState: new LocalState({ cache } as any),
  link: ApolloLink.split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    wsLink,
    // Keep the auth guard ahead of both logging and transport. A terminal
    // refresh failure must not fan out into GraphQL or unauthenticated logs.
    ApolloLink.from([authLink, errorLink, uploadLink]),
  ),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    },
  },
});

export default client;
